import React, { useRef, useEffect, useState } from 'react'
import styled, { keyframes } from 'styled-components'
import theme from 'stories/utils/theme'
import { Body1 } from 'stories/elements/Typography/Text'
import QuatationMark from 'assets/homepage-redesign/quatation-mark.svg'
import StarIcon from 'assets/homepage-redesign/star.svg'

const scrollReviews = keyframes`
  from {
    transform: translateX(0);
  }
  to {
    transform: translateX(calc(-50%));
  }
`

const Container = styled.div`
    padding: 0;
    overflow: hidden;
    width: 100%;
    position: relative;
    margin: 0 auto;
    background: ${theme.colors.transparent};
    @media (max-width: 940px) {
        border-radius: 0px;
    }
`

const ReviewsContainer = styled.div`
    position: relative;
    overflow: hidden;
    padding: 0;
    width: 100%;

    @media (max-width: 940px) {
        overflow-x: auto;
        scrollbar-width: none; /* Firefox */
        -ms-overflow-style: none; /* IE and Edge */
        &::-webkit-scrollbar {
            display: none; /* Chrome, Safari, Opera */
        }
    }
`

const ScrollingReviews = styled.div<{ width: string; animationState: string }>`
    display: flex;
    width: ${(props) => props.width};

    @media (min-width: 941px) {
        animation: ${scrollReviews} 60s linear infinite;
        animation-play-state: ${(props) => props.animationState};
    }

    @media (max-width: 940px) {
        animation: none;
        width: max-content;
    }
`

const ReviewGroup = styled.div`
    display: flex;
    flex-shrink: 0;

    @media (max-width: 940px) {
        padding: 0 16px 0 24px;
        &:last-child {
            display: none;
        }
    }
`

const ReviewCard = styled.div`
    min-width: 440px;
    max-width: 390px;
    min-height: 150px;
    padding: 15px 20px;
    background: ${theme.colors.backgroundTransparent};
    border-radius: ${theme.borderRadius.webappCard};
    box-shadow: 0px 4px 10px rgba(0, 0, 0, 0.1);
    margin: 8px 8px 0 0;
    box-sizing: border-box;
    display: flex;
    flex-direction: column;
    gap: 20px;
    position: relative;
    @media (max-width: 750px) {
        min-width: 312px;
        width: 312px;
        flex: 0 0 312px;
        padding: 14px;
        margin: 8px 8px 0 0;
    }
`

const ReviewContent = styled.div`
    flex: 1;
    overflow-y: auto;
    p {
        font-family: var(--font-inter);
        font-size: 14px;
        line-height: 24px;
        font-weight: 450;
        color: #f9f9f9;
        letter-spacing: 0.4px;
        @media (max-width: 750px) {
            font-size: 14px;
            line-height: 20px;
            font-weight: 450;
            letter-spacing: 0.4px;
        }
    }
`

const UserInfo = styled.div`
    display: flex;
    flex-direction: column;
    width: fit-content;
    gap: 5px;
    padding-bottom: 5px;
    p {
        font-family: var(--font-inter);
        font-size: 14px;
        line-height: 20px;
        font-weight: 650;
        letter-spacing: 0.4px;
    }
    .review-description {
        font-family: var(--font-inter);
        font-size: 11px;
        line-height: 16px;
        font-weight: 400;
        letter-spacing: 0.5px;
        color: #ffffffb8;
    }
`

const StarsContainer = styled.div`
    display: flex;
    position: absolute;
    top: 15px;
    right: 20px;
    gap: 0px;
    svg {
        height: 36px;
        width: 34px;
        scale: 0.475;
        position: relative;
        margin-right: -12.5px;
    }
    @media (max-width: 750px) {
        top: 10px;
        right: 20px;
    }
`

interface Review {
    name: string
    text: string
    stars: number
}

interface RotatingReviewsProps {
    reviews: Review[]
    hideStars?: boolean
}

const RotatingReviews = ({ reviews, hideStars }: RotatingReviewsProps) => {
    const scrollContainerRef = useRef<HTMLDivElement>(null)
    const [containerWidth, setContainerWidth] = useState('100%')
    const [animationState, setAnimationState] = useState('paused')
    const [isMobile, setIsMobile] = useState(false)

    useEffect(() => {
        const checkScreenSize = () => {
            setIsMobile(window.innerWidth <= 940)
        }

        checkScreenSize()
        window.addEventListener('resize', checkScreenSize)

        if (scrollContainerRef.current) {
            setContainerWidth(scrollContainerRef.current.scrollWidth + 'px')
            setAnimationState('running')
        }

        return () => window.removeEventListener('resize', checkScreenSize)
    }, [])

    const renderStars = (count: number) => {
        return Array.from({ length: 5 }, (_, i) => <StarIcon key={i} filled={i < count} />)
    }

    const reviewItems = reviews.map((review, index) => {
        const nameParts = review.name.split(',')
        const name = nameParts[0].trim()
        const description = nameParts.length > 1 ? nameParts[1].trim() : ''

        return (
            <ReviewCard key={`review-${index}`}>
                {!hideStars && (
                    <>
                        <StarsContainer>{renderStars(review.stars)}</StarsContainer>
                        <QuatationMark />
                    </>
                )}
                <ReviewContent>
                    <Body1 fontFamily="var(--font-inter)">{review.text}</Body1>
                </ReviewContent>
                <UserInfo>
                    <Body1 color={theme.colors.white} fontSize={16} fontFamily="var(--font-inter)" fontWeight={500}>
                        {name}
                    </Body1>
                    {description && (
                        <Body1
                            color={theme.colors.grayScaleSecondary}
                            fontSize={14}
                            fontFamily="var(--font-inter)"
                            fontWeight={400}
                            className="review-description"
                        >
                            {description}
                        </Body1>
                    )}
                </UserInfo>
            </ReviewCard>
        )
    })

    return (
        <Container>
            <ReviewsContainer>
                <ScrollingReviews
                    ref={scrollContainerRef}
                    width={containerWidth}
                    animationState={animationState}
                    onMouseEnter={() => !isMobile && setAnimationState('paused')}
                    onMouseLeave={() => !isMobile && setAnimationState('running')}
                >
                    <ReviewGroup>{reviewItems}</ReviewGroup>
                    <ReviewGroup>{reviewItems}</ReviewGroup>
                </ScrollingReviews>
            </ReviewsContainer>
        </Container>
    )
}

export default RotatingReviews
